/**
 * Cart Item Total
 * 
 * This script adds the total per item calculation to the cart sidebar
 * when users add multiple of the same item.
 */

class CartItemTotal {
  constructor() {
    this.init();
  }

  init() {
    // Initialize when the DOM is fully loaded
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        this.setupEventListeners();
        this.updateAllItemTotals();
      });
    } else {
      this.setupEventListeners();
      this.updateAllItemTotals();
    }
  }

  setupEventListeners() {
    // Listen for cart updates
    document.addEventListener('cart:updated', this.updateAllItemTotals.bind(this));
    
    // Listen for quantity changes
    document.addEventListener('change', (event) => {
      if (event.target.classList.contains('quantity__input')) {
        this.updateAllItemTotals();
      }
    });

    // Listen for cart drawer opening
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      cartDrawer.addEventListener('transitionend', (event) => {
        if (event.target === cartDrawer && cartDrawer.classList.contains('active')) {
          this.updateAllItemTotals();
        }
      });
    }
    
    // Also update when DOM content changes (for when items are added/removed)
    const cartItemsContainer = document.querySelector('cart-drawer-items');
    if (cartItemsContainer) {
      const observer = new MutationObserver(this.updateAllItemTotals.bind(this));
      observer.observe(cartItemsContainer, { childList: true, subtree: true });
    }
  }

  updateAllItemTotals() {
    // Wait a moment for the cart to fully update
    setTimeout(() => {
      const cartItems = document.querySelectorAll('.cart-item');
      
      if (!cartItems.length) return;
      
      cartItems.forEach(item => {
        this.updateItemTotal(item);
      });
    }, 100);
  }

  updateItemTotal(cartItem) {
    // Get the quantity input
    const quantityInput = cartItem.querySelector('.quantity__input');
    if (!quantityInput) return;
    
    const quantity = parseInt(quantityInput.value, 10);
    if (isNaN(quantity) || quantity <= 1) {
      // If quantity is 1 or less, no need to show the total
      this.removeItemTotal(cartItem);
      return;
    }
    
    // Try different price selectors to handle various theme configurations
    let priceElement = cartItem.querySelector('.custom-item-price');
    let priceText = '';
    
    if (priceElement) {
      // First check for sale price
      const finalPrice = priceElement.querySelector('.cart-item__final-price');
      if (finalPrice) {
        priceText = finalPrice.textContent.trim();
      } else {
        // Otherwise use the regular price
        priceText = priceElement.textContent.trim();
      }
    } else {
      // Fallback to other common price selectors
      priceElement = cartItem.querySelector('.cart-item__price-wrapper');
      if (priceElement) {
        const finalPrice = priceElement.querySelector('.cart-item__final-price, .price--end, .product-option');
        if (finalPrice) {
          priceText = finalPrice.textContent.trim();
        } else {
          priceText = priceElement.textContent.trim();
        }
      }
    }
    
    // If we still don't have a price, try one more general approach
    if (!priceText) {
      const allPriceElements = cartItem.querySelectorAll('[class*="price"]');
      for (const el of allPriceElements) {
        if (el.textContent.trim().match(/[\d.,]+/)) {
          priceText = el.textContent.trim();
          break;
        }
      }
    }
    
    // If we still couldn't find a price, exit
    if (!priceText) return;
    
    // Parse the price (remove currency symbol and convert to number)
    const price = this.extractPrice(priceText);
    if (isNaN(price)) return;
    
    // Calculate the total
    const total = price * quantity;
    
    // Format the total with the same currency format
    const currencyFormat = priceText.replace(/[\d,.]+/g, '').trim();
    const formattedTotal = this.formatPrice(total, currencyFormat);
    
    // Add or update the total element
    this.addOrUpdateItemTotal(cartItem, formattedTotal);
  }

  extractPrice(priceText) {
    // Remove currency symbols and convert to number
    // Handle both comma and period as decimal separators
    let cleanPrice = priceText.replace(/[^\d,.]/g, '');
    
    // Determine if comma or period is the decimal separator
    // If there are both commas and periods, the last one is likely the decimal separator
    if (cleanPrice.includes(',') && cleanPrice.includes('.')) {
      const lastCommaIndex = cleanPrice.lastIndexOf(',');
      const lastPeriodIndex = cleanPrice.lastIndexOf('.');
      
      if (lastCommaIndex > lastPeriodIndex) {
        // Comma is the decimal separator
        cleanPrice = cleanPrice.replace(/\./g, '').replace(',', '.');
      } else {
        // Period is the decimal separator
        cleanPrice = cleanPrice.replace(/,/g, '');
      }
    } else if (cleanPrice.includes(',')) {
      // Only commas present, treat as decimal separator
      cleanPrice = cleanPrice.replace(',', '.');
    }
    
    return parseFloat(cleanPrice);
  }

  formatPrice(price, currencyFormat) {
    // Get the current money format from the shop
    // This uses the same format as the individual item price
    const moneyFormat = currencyFormat.trim();
    
    // Format the number with 2 decimal places
    const formattedNumber = price.toFixed(2);
    
    // Check if the currency symbol is at the beginning or end
    if (moneyFormat.charAt(0).match(/[^\d,.]/)) {
      // Currency symbol at the beginning (e.g., $10.00)
      return `${moneyFormat}${formattedNumber}`;
    } else {
      // Currency symbol at the end (e.g., 10.00€)
      return `${formattedNumber}${moneyFormat}`;
    }
  }

  addOrUpdateItemTotal(cartItem, formattedTotal) {
    let totalElement = cartItem.querySelector('.cart-item__total');
    
    if (!totalElement) {
      // Create the total element if it doesn't exist
      totalElement = document.createElement('div');
      totalElement.classList.add('cart-item__total');
      
      // Find the best place to insert the total
      // First try the price wrapper
      let insertAfter = cartItem.querySelector('.cart-item__price-wrapper');
      
      // If that doesn't exist, try the quantity wrapper
      if (!insertAfter) {
        insertAfter = cartItem.querySelector('.cart-item__quantity-wrapper');
      }
      
      // If neither exists, try the cart item details
      if (!insertAfter) {
        insertAfter = cartItem.querySelector('.cart-item__details');
      }
      
      // Insert the total element
      if (insertAfter) {
        // If inserting after a child element
        if (insertAfter.parentNode) {
          insertAfter.parentNode.insertBefore(totalElement, insertAfter.nextSibling);
        } else {
          // If inserting at the end of a container
          cartItem.appendChild(totalElement);
        }
      } else {
        // Last resort - append to the cart item
        cartItem.appendChild(totalElement);
      }
    }
    
    // Set the content with a clear label
    totalElement.innerHTML = `Item total: <strong>${formattedTotal}</strong>`;
  }

  removeItemTotal(cartItem) {
    const totalElement = cartItem.querySelector('.cart-item__total');
    if (totalElement) {
      totalElement.remove();
    }
  }
}

// Initialize the cart item total functionality
new CartItemTotal();